import dayjs, {Dayjs} from 'dayjs';
import {json, JsonMapper, JsonMappers} from '../core/data-source/json.mapper';
import {ComputedRef} from 'vue';

export enum DayShift {
    sameDay = 'sameDay',
    previousDay = 'previousDay',
    nextDay = 'nextDay',
    dayAfterTomorrow = 'dayAfterTomorrow',
    dayPlus3 = 'dayPlus3',
    dayPlus4 = 'dayPlus4'
}

export const dayShiftMapping: any = {
    '0': DayShift.sameDay,
    '1': DayShift.nextDay,
    '2': DayShift.dayAfterTomorrow,
    '3': DayShift.dayPlus3,
    '4': DayShift.dayPlus4,
    '-1': DayShift.previousDay
};

export function getDayShiftNumber(dayShift: DayShift): number {
    return Number(Object.keys(dayShiftMapping)[Object.values(dayShiftMapping).indexOf(dayShift)]);
}

const availabilityDaysIndexMap = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7
};
const dateFormat: string = 'DD/MM/YYYY HH:mm';
const timeFormat: string = 'HH:mm';

const DateMapper: JsonMapper = {
    from: (value: string): Dayjs => {
        return dayjs.utc(value, dateFormat);
    },
    to: (value: Dayjs): string => {
        return value ? value.format(dateFormat) : '';
    }
};

const TimeMapper: JsonMapper = {
    from: (value: string): Dayjs => {
        let val = dayjs.utc(value, timeFormat);
        return dayjs.utc(0).set('hour', val.get('hour')).set('minute', val.get('minute'));
    },
    to: (value: Dayjs): string => {
        return value ? value.format(timeFormat) : '';
    }
};

export class DriverAvailabilityRecord {
    @json({
        value: true
    })
    available: boolean;

    @json({
        jsonName: 'day_of_week'
    })
    dayOfWeek: string;

    @json({
        jsonName: 'break_start_time',
        mapper: TimeMapper
    })
    breakStartTime: Dayjs;

    @json({
        jsonName: 'break_end_time',
        mapper: TimeMapper
    })
    breakEndTime: Dayjs;

    @json({
        jsonName: 'start_time',
        value: () => dayjs.utc(0).clone(),
        mapper: {
            from: (value: string): Dayjs => {
                return DateMapper.from(value).set('date', 1).set('year', 1970);
            },
            to: (value: Dayjs, data: any): any => {
                return dayjs
                    .utc(0)
                    .clone()
                    .add(getDayShiftNumber(data.startDay), 'day')
                    .set('hour', value.get('hour'))
                    .set('minute', value.get('minute'))
                    .format(dateFormat);
            }
        }
    })
    startTime: Dayjs;

    @json({
        jsonName: 'end_time',
        value: () => dayjs.utc(0).endOf('day').clone(),
        mapper: {
            from: (value: string): Dayjs => {
                return DateMapper.from(value).set('date', 1).set('year', 1970);
            },
            to: (value: Dayjs, data: any): any => {
                return dayjs
                    .utc(0)
                    .endOf('day')
                    .clone()
                    .add(getDayShiftNumber(data.endDay), 'day')
                    .set('hour', value.get('hour'))
                    .set('minute', value.get('minute'))
                    .format('DD/MM/YYYY HH:mm');
            }
        }
    })
    endTime: Dayjs;

    @json({
        skip: true,
        jsonName: 'start_time',
        value: DayShift.sameDay,
        mapper: {
            from: (value: string): DayShift => {
                let date: Dayjs = value ? DateMapper.from(value) : dayjs.utc(0);
                return date.valueOf() < 0 ? DayShift.previousDay : dayShiftMapping[date.date() - 1];
            }
        }
    })
    startDay: DayShift;

    @json({
        skip: true,
        jsonName: 'end_time',
        value: DayShift.sameDay,
        mapper: {
            from: (value: string): DayShift => {
                let date: Dayjs = value ? DateMapper.from(value) : dayjs.utc(0).endOf('day');
                return date.valueOf() < 0 ? DayShift.previousDay : dayShiftMapping[date.date() - 1];
            }
        }
    })
    endDay: DayShift;

    @json({
        skip: true,
        jsonName: 'day_of_week',
        mapper: {
            from: (value: string) => {
                return availabilityDaysIndexMap[value];
            }
        }
    })
    numberDayOfWeek: number;

    @json({
        jsonName: 'fixed_work_day_start'
    })
    fixedWorkDayStart: boolean;

    @json({
        skip: true,
        value: false
    })
    multiDayInvalid: boolean;
}
